import { useCallback, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import useRoomContext from '@src/contexts/useRoomContext';
import { useDrawerStore, useRoomActionsStore, useVirtualBackgroundStore, useRoomStore } from '@src/stores';

import { TBackgroundEffect } from '@src/types';

/**
 * Background Effects Modal Hook
 *
 * Manages state and logic for the virtual background effects modal/drawer.
 * Handles effect selection, application, and removal workflows.
 *
 * **Purpose:**
 * - Provides modal/drawer state management (open/close)
 * - Handles temporary effect selection (before apply)
 * - Manages effect application and removal
 * - Determines Apply button enabled/disabled state
 * - Provides preview enable flag for mobile (drawer) and desktop (modal)
 *
 * **Usage Context:**
 * - Used in both pre-join (lobby) and in-meeting screens
 * - Works with BackgroundEffectsModal (desktop) and VirtualBackground (mobile drawer)
 * - Integrates with virtualBackgroundStore for state persistence
 *
 * **Flow:**
 * 1. User opens modal/drawer → `isBackgroundEffectsOpen` or drawer state updates
 * 2. User selects effect → `selectedBackgroundEffect` updates (temporary)
 * 3. User clicks Apply → `handleSaveSelectedBackground` commits to store
 * 4. User clicks Close → Modal closes, temporary selection discarded
 * 5. User clicks Remove → Effect cleared
 *
 * @returns {Object} Modal state and handlers
 * @returns {boolean} isPreviewEffectEnabled - Whether preview should be active (mobile-aware)
 * @returns {Function} toggleDrawer - Controls drawer open/close state (for mobile)
 *
 * @example
 * // Desktop modal usage
 * const { isBackgroundEffectsOpen, selectedBackgroundEffect, ... } = useBackgroundEffectsModal();
 *
 * // Mobile drawer usage
 * const { isPreviewEffectEnabled, toggleDrawer, ... } = useBackgroundEffectsModal();
 */
const useBackgroundEffectsModal = () => {
  const { currentPeer, isMobileDevice } = useRoomContext();

  const { isCamClosed } = useRoomActionsStore(
    useShallow((state) => ({
      isCamClosed: state.camSettings.isCamClosed,
    }))
  );

  const { isDrawerOpen, selectedKey, toggleDrawer } = useDrawerStore(
    useShallow((state) => ({
      isDrawerOpen: state.isDrawerOpen,
      selectedKey: state.selectedKey,
      toggleDrawer: state.toggleDrawer,
    }))
  );
  const { peerName } = useRoomStore(
    useShallow((state) => ({
      peerName: state.peerName,
    }))
  );

  // VB store
  const {
    setSelectedEffect,
    applySelectedEffect,
    uploadedBackgrounds,
    discardSelectedEffect,
    isBackgroundEffectsOpen,
    setIsBackgroundEffectsOpen,
    selectedEffect: selectedBackgroundEffect,
  } = useVirtualBackgroundStore(
    useShallow((state) => ({
      selectedEffect: state.selectedEffect,
      setSelectedEffect: state.setSelectedEffect,
      uploadedBackgrounds: state.uploadedBackgrounds,
      applySelectedEffect: state.applySelectedEffect,
      discardSelectedEffect: state.discardSelectedEffect,
      isBackgroundEffectsOpen: state.isBackgroundEffectsOpen,
      setIsBackgroundEffectsOpen: state.setIsBackgroundEffectsOpen,
    }))
  );

  // Select effect (Preview only)
  const onSelectBackgroundEffect = useCallback(
    (effect: TBackgroundEffect) => {
      setSelectedEffect(effect);
    },
    [setSelectedEffect]
  );

  // Remove effect (Sets selection to none)
  const removeBackgroundEffect = useCallback(() => {
    setSelectedEffect({ type: 'none', value: null });
  }, [setSelectedEffect]);

  const isApplyButtonDisabled = useMemo(() => isCamClosed, [isCamClosed]);

  const isPreviewEffectEnabled = useMemo(
    () =>
      isMobileDevice
        ? isDrawerOpen && selectedKey === 'virtual-background' && !isCamClosed
        : isBackgroundEffectsOpen && !isCamClosed,
    [isBackgroundEffectsOpen, isCamClosed, isDrawerOpen, isMobileDevice, selectedKey]
  );

  // Apply button - Commit selection to applied, then close
  const handleSaveSelectedBackground = useCallback(() => {
    applySelectedEffect();
    setIsBackgroundEffectsOpen(false);
  }, [applySelectedEffect, setIsBackgroundEffectsOpen]);

  // Close modal - Reverts selection (handled by store setter)
  const handleCloseModal = useCallback(() => {
    discardSelectedEffect();
    setIsBackgroundEffectsOpen(false);
  }, [discardSelectedEffect, setIsBackgroundEffectsOpen]);

  const handleOpenEffectsModal = useCallback(() => {
    setIsBackgroundEffectsOpen(true);
  }, [setIsBackgroundEffectsOpen]);

  const handleApplyChanges = useCallback(() => {
    handleSaveSelectedBackground();
    setIsBackgroundEffectsOpen(false);
  }, [handleSaveSelectedBackground, setIsBackgroundEffectsOpen]);

  return {
    peerName,
    currentPeer,
    isCamClosed,
    toggleDrawer,
    handleCloseModal,
    handleApplyChanges,
    uploadedBackgrounds,
    isApplyButtonDisabled,
    removeBackgroundEffect,
    handleOpenEffectsModal,
    isPreviewEffectEnabled,
    isBackgroundEffectsOpen,
    selectedBackgroundEffect,
    onSelectBackgroundEffect,
    setIsBackgroundEffectsOpen,
    handleSaveSelectedBackground,
  };
};

export default useBackgroundEffectsModal;
